import { useEffect, useRef, useState } from 'react';
import { Stack, breakpoints, Inline, LinkButton } from '@etg/wings';
import { useDateFormat, useProperty, useTranslation } from '@eti/providers';
import { useFormContext } from 'react-hook-form';
import { css } from '@eti/styles';
import { m as motion } from 'framer-motion';
import { CaretDown } from '@phosphor-icons/react';
import { useMediaQuery } from 'usehooks-ts';
import RebookingBanner from '../../../common/components/RebookingBanner';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { useToggle } from '../../../common/custom-hooks/toggle';
import { RESULT_PAGE_VERSION_3 } from '../../../pages/result/constants/resultConstants';
import { DEPARTURE_DATE, SINGLE_BOUND, TRIP_TYPE } from '../constants/formFields';
import type { DateFormat } from '../types/dateFormat';
import type {
  RebookingData,
  SearchFormValues,
} from '../../../common/search-form-panel/SearchFormNewContainer';
import {
  extendedSearchFormStyles,
  iconStyles,
  linkButtonStyles,
  motionVariantsExtendedOptions,
  datesAndPassengersTransition,
} from './styles/searchFormStyles';
import PassengerAndCabinClassNew from './PassengersAndCabinClassNew';
import DatesNew from './DatesNew';
import SubmitButtonNew from './SubmitButtonNew';
import BoundNew from './BoundNew';
import TripTypesNew from './TripTypesNew';
import BoundsNew from './BoundsNew';
import DirectFlightCheckBoxNew from './DirectFlightCheckBoxNew';
import StopoverSelectNew from './StopoverSelectNew';
import ExtendedSearchFormFieldsNew from './ExtendedSearchFormFieldsNew';
import ZeroWidthSpace from './ZeroWidthSpace';

const formWrapperStyles = (isMultiStop: boolean) => css`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: ${isMultiStop ? 'column' : 'row'};
  }
`;

const wideFlexStyles = css`
  flex: 3;
`;

type SearchFormNewProps = {
  rebookingData?: RebookingData;
  shouldShowRebookingBanner?: boolean;
};

const SearchFormNew = ({ rebookingData, shouldShowRebookingBanner }: SearchFormNewProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { date } = useDateFormat() as { date: DateFormat };
  const { watch } = useFormContext<SearchFormValues>();
  const [
    selectedTripType,
    origin,
    destination,
    returnTimeOfDay,
    departureTimeOfDay,
    selectedCarriers,
  ] = watch([
    'tripType',
    'singleBound.origin',
    'singleBound.destination',
    'singleBound.returnTimeOfDay',
    'singleBound.departureTimeOfDay',
    'carrierCodes',
  ]);
  const [areOriginAndDestinationFilled, setAreOriginAndDestinationFilled] = useState(
    Boolean(origin && destination),
  );
  const { isOpen: isMoreOptionsOpen, handleToggle } = useToggle(
    Boolean(returnTimeOfDay || departureTimeOfDay || selectedCarriers),
  );
  const formConstraints = rebookingData?.formConstraints || null;
  const isMultiStop = selectedTripType === tripTypes.MULTI_STOP;
  const submitButtonRef = useRef(null);
  const departureInputRef = useRef<HTMLInputElement>(null);
  const stopoverSelectRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (origin && destination) {
      setAreOriginAndDestinationFilled(true);
    }
  }, [origin, destination]);

  const isMobile = useMediaQuery(`(max-width: ${breakpoints._768})`);

  const getFieldName = (fieldName: string) =>
    selectedTripType !== tripTypes.MULTI_STOP ? `${SINGLE_BOUND}.${fieldName}` : '';
  const isExtendedSearchFormEnabled = p('SearchForm.ShowExtendedForm.Enabled');
  const searchFormStopoverEnabled = p('Searchform.Stopover.Enabled');
  const resultPageVersion = String(p('Result.Page.Version'));
  const isStopoverEnabled =
    searchFormStopoverEnabled && resultPageVersion === RESULT_PAGE_VERSION_3;

  const onDatesAnimationStart = () => {
    const isSearchButtonFocused = document.activeElement === submitButtonRef.current;
    const isStopoverSelectFocused = document.activeElement === stopoverSelectRef.current;

    if (isSearchButtonFocused || isStopoverSelectFocused) {
      departureInputRef.current?.focus();
    }
  };

  return (
    <>
      {rebookingData && shouldShowRebookingBanner && (
        <RebookingBanner rebookingData={rebookingData} shouldShowRebookingCriteria />
      )}
      <Stack spacing={24}>
        <TripTypesNew fieldName={TRIP_TYPE} />
        <Stack className={formWrapperStyles(isMultiStop)} spacing={24}>
          <Stack
            className={wideFlexStyles}
            spacing={areOriginAndDestinationFilled || isMultiStop ? 24 : 0}
          >
            {!isMultiStop ? (
              <>
                <BoundNew
                  boundIndex={0}
                  dateFormat={date}
                  selectedTripType={selectedTripType}
                  setAreOriginAndDestinationFilled={setAreOriginAndDestinationFilled}
                />
                {(areOriginAndDestinationFilled || isMultiStop) && (
                  <motion.div
                    animate={datesAndPassengersTransition.animate}
                    data-testid="date-time-animation-wrapper"
                    initial={datesAndPassengersTransition.initial}
                    onAnimationStart={onDatesAnimationStart}
                    transition={datesAndPassengersTransition.transition}
                  >
                    <Inline
                      collapseBelow={breakpoints._768}
                      spacing={{
                        [breakpoints._0]: 24,
                        [breakpoints._768]: 48,
                      }}
                    >
                      <DatesNew
                        boundIndex={0}
                        dateFormat={date}
                        departureDateName={getFieldName(DEPARTURE_DATE)}
                        departureInputRef={departureInputRef}
                        selectedTripType={selectedTripType}
                      />
                      <PassengerAndCabinClassNew formConstraints={formConstraints} />
                    </Inline>
                    {isExtendedSearchFormEnabled && (
                      <div className={extendedSearchFormStyles}>
                        <Inline spacing={8}>
                          <LinkButton
                            className={linkButtonStyles}
                            data-testid="more-search-options-button"
                            onClick={handleToggle}
                          >
                            {t('Searchform.MoreSearchOptions.Link.Text')}
                            <CaretDown
                              className={iconStyles(isMoreOptionsOpen)}
                              size={20}
                              weight="light"
                            />
                          </LinkButton>
                        </Inline>
                        <motion.div
                          animate={isMoreOptionsOpen ? 'show' : 'hide'}
                          data-testid="more-options-animation-wrapper"
                          initial={false}
                          variants={motionVariantsExtendedOptions}
                        >
                          <Stack spacing={24}>
                            <ExtendedSearchFormFieldsNew selectedTripType={selectedTripType} />
                            {!isStopoverEnabled && <DirectFlightCheckBoxNew />}
                          </Stack>
                        </motion.div>
                      </div>
                    )}
                  </motion.div>
                )}
              </>
            ) : (
              <>
                <BoundsNew dateFormat={date} selectedTripType={selectedTripType} />
                <Inline
                  collapseBelow={breakpoints._768}
                  spacing={{
                    [breakpoints._0]: 24,
                    [breakpoints._768]: 48,
                  }}
                >
                  {!isStopoverEnabled && (
                    <Stack spacing={4}>
                      <ZeroWidthSpace isDesktopOnly />
                      <DirectFlightCheckBoxNew />
                    </Stack>
                  )}
                  <PassengerAndCabinClassNew formConstraints={formConstraints} />
                  {isMobile && isStopoverEnabled && (
                    <StopoverSelectNew
                      checkboxRef={stopoverSelectRef}
                      withTopSpacing={!isMultiStop}
                    />
                  )}
                  <SubmitButtonNew
                    isMultiStop
                    isStopoverEnabled={isStopoverEnabled}
                    ref={submitButtonRef}
                  />
                </Inline>
              </>
            )}
            {(!isMultiStop || !isMobile) && isStopoverEnabled && (
              <StopoverSelectNew
                checkboxRef={stopoverSelectRef}
                withTopSpacing={!isMultiStop && !areOriginAndDestinationFilled}
              />
            )}
          </Stack>
          {!isMultiStop && (
            <SubmitButtonNew
              areOriginAndDestinationFilled={areOriginAndDestinationFilled}
              isStopoverEnabled={isStopoverEnabled}
              ref={submitButtonRef}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default SearchFormNew;
